/**
 * Emergency Drill Log Tab Component
 * For documenting mandatory annual emergency drills at licensed childcare centers
 * Features: Form generation, save to database, print-friendly, PDF export
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, FileText, Printer, Save, Trash2, Download, Plus, CheckCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DRILL_TYPES = [
  "Fire Drill",
  "Tornado Drill",
  "Flood Drill",
  "Earthquake Drill",
  "Gas Leak",
  "Chemical Spill",
  "Shelter in Place",
  "Lockdown - Intruder",
  "Lockdown - External Threat",
  "Evacuation Drill",
  "Power Outage",
  "Medical Emergency",
  "Impaired Adult",
  "Other"
];

export const EmergencyDrillLogTab = ({ centerName = "", organization = {}, onUpload, paperCopySection = null }) => {
  const [drillLogs, setDrillLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    centerName: centerName || organization?.name || "",
    licenseNumber: organization?.licenseNumber || "",
    address: organization?.address || "",
    phone: organization?.phone || "",
    drillDate: new Date().toISOString().split('T')[0],
    drillTime: "",
    amPm: "AM",
    durationMinutes: "",
    drillType: "",
    otherDrillType: "",
    expirationDate: "",
    participatingStaff: "",
    specialAccommodations: "",
    childrenCount: "",
    totalParticipants: "",
    problemsObservations: "",
    signatureName: "",
    signaturePosition: "",
    signatureDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchDrillLogs();
  }, []);

  useEffect(() => {
    if (centerName || organization?.name) {
      setFormData(prev => ({
        ...prev,
        centerName: centerName || organization?.name || prev.centerName,
        licenseNumber: organization?.licenseNumber || prev.licenseNumber,
        address: organization?.address || prev.address,
        phone: organization?.phone || prev.phone
      }));
    }
  }, [centerName, organization]);

  const fetchDrillLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/emergency-drill-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrillLogs(response.data || []);
    } catch (error) {
      console.error("Error fetching drill logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.drillDate || !formData.drillType || !formData.durationMinutes || !formData.expirationDate) {
      toast.error("Please fill in required fields: Date, Expiration Date, Drill Type, and Duration");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/emergency-drill-logs`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Emergency drill log saved successfully!");
      setDrillLogs(prev => [response.data, ...prev]);
      
      // Reset form for next entry
      setFormData(prev => ({
        ...prev,
        drillDate: new Date().toISOString().split('T')[0],
        drillTime: "",
        durationMinutes: "",
        drillType: "",
        otherDrillType: "",
        expirationDate: "",
        participatingStaff: "",
        specialAccommodations: "",
        childrenCount: "",
        totalParticipants: "",
        problemsObservations: "",
        signatureName: "",
        signaturePosition: "",
        signatureDate: new Date().toISOString().split('T')[0]
      }));
    } catch (error) {
      toast.error("Failed to save drill log");
      console.error("Error saving drill log:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm("Are you sure you want to delete this drill log?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/emergency-drill-logs/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrillLogs(prev => prev.filter(log => log.id !== logId));
      toast.success("Drill log deleted");
    } catch (error) {
      toast.error("Failed to delete drill log");
    }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    if (!w) { toast.error("Allow popups to print"); return; }
    const drillTypeDisplay = formData.drillType === "Other" ? (formData.otherDrillType || "Other") : formData.drillType;
    w.document.write(`<!DOCTYPE html><html><head><title>Emergency Drill Log - ${formData.centerName || ''}</title>
    <style>body{font-family:Arial,sans-serif;padding:30px;max-width:800px;margin:0 auto;font-size:12px;}
    h1{text-align:center;font-size:18px;}.field{margin-bottom:8px;}.fl{font-weight:bold;}
    .header{text-align:center;border:2px solid #000;padding:10px;margin-bottom:16px;}
    @media print{body{padding:10px;}}</style></head><body>
    <div class="header"><h1>EMERGENCY DRILL LOG</h1>
    <p><strong>${formData.centerName || '[Center Name]'}</strong> | License #: ${formData.licenseNumber || '___'}</p>
    <p>${formData.address || '[Address]'} | Phone: ${formData.phone || '___'}</p></div>
    <div class="field"><span class="fl">Date Drill Conducted:</span> ${formData.drillDate || '___'}</div>
    <div class="field"><span class="fl">Time:</span> ${formData.drillTime || '___'} ${formData.amPm || ''}</div>
    <div class="field"><span class="fl">Duration (Minutes):</span> ${formData.durationMinutes || '___'}</div>
    <div class="field"><span class="fl">Type of Drill:</span> ${drillTypeDisplay || '___'}</div>
    <div class="field"><span class="fl">Participating Staff:</span> ${formData.participatingStaff || '___'}</div>
    <div class="field"><span class="fl">Children Participating:</span> ${formData.childrenCount || '___'}</div>
    <div class="field"><span class="fl">Total Participants:</span> ${formData.totalParticipants || '___'}</div>
    <div class="field"><span class="fl">Special Accommodations:</span> ${formData.specialAccommodations || '___'}</div>
    <div class="field"><span class="fl">Problems/Observations:</span> ${formData.problemsObservations || '___'}</div>
    <div class="field" style="margin-top:20px;border-top:1px solid #000;padding-top:10px;">
      <span class="fl">Signed:</span> ${formData.signatureName || '___'} | ${formData.signaturePosition || '___'} | ${formData.signatureDate || '___'}
    </div>
    </body></html>`);
    w.document.close();
    w.print();
  };

  const loadPreviousLog = (log) => {
    setFormData({
      ...log,
      drillDate: new Date().toISOString().split('T')[0],
      signatureDate: new Date().toISOString().split('T')[0]
    });
    toast.success("Previous log loaded - update the details for new entry");
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF, DOC, DOCX, or image file (JPG, PNG)");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('centerName', formData.centerName);
      // Uploaded files are plain attachments — no date is copied from the drill form.
      
      // Upload as a compliance document
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/emergency-drill-logs/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      
      toast.success("Emergency drill document uploaded successfully!");
      onUpload?.();  // refresh the parent's Uploaded Documents list immediately
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-blue-800 flex items-center gap-3">
                <AlertTriangle className="w-7 h-7" />
                Emergency Drill Log
              </CardTitle>
              <p className="text-blue-600 text-sm mt-1">Document emergency drills - print-ready format</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handlePrint} variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Paper Copy on File — internal section directly below the heading/Print row */}
          {paperCopySection}
          {/* File Upload Section */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload Emergency Drill Document
            </h3>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                dragOver 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 bg-gray-50'
              }`}
              onClick={() => document.getElementById('drill-file-input').click()}
            >
              <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-blue-600' : 'text-gray-400'}`} />
              <p className={`text-sm ${dragOver ? 'text-blue-600' : 'text-gray-500'}`}>
                {uploading ? 'Uploading...' : dragOver ? 'Drop file here' : 'Click to upload or drag & drop your emergency drill document here'}
              </p>
              <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, JPG, PNG (Max 10MB)</p>
            </div>
            <input
              id="drill-file-input"
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => handleFileUpload(e.target.files?.[0])}
              className="hidden"
            />
          </div>

          {/* Preloaded Template Notice — informational only */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="preloaded-drill-log-notice">
            <FileText className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              A preloaded Emergency Drill Log is available below for your convenience. You may use the provided log or upload your own completed log.
            </p>
          </div>

          {/* Center Information */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Registered Childcare Center Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Center Name</Label>
                <Input
                  value={formData.centerName}
                  onChange={(e) => handleInputChange("centerName", e.target.value)}
                  className="mt-1"
                  placeholder="Enter childcare center name"
                />
              </div>
              <div>
                <Label className="text-gray-700">License Number</Label>
                <Input
                  value={formData.licenseNumber}
                  onChange={(e) => handleInputChange("licenseNumber", e.target.value)}
                  className="mt-1"
                  placeholder="Enter license number"
                />
              </div>
              <div>
                <Label className="text-gray-700">Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="mt-1"
                  placeholder="Enter center address"
                />
              </div>
              <div>
                <Label className="text-gray-700">Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className="mt-1"
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </div>

          {/* Drill Information */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3">Drill Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-700">Date Drill Conducted *</Label>
                <Input
                  type="date"
                  value={formData.drillDate}
                  onChange={(e) => handleInputChange("drillDate", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700">Expiration Date *</Label>
                <Input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => handleInputChange("expirationDate", e.target.value)}
                  className="mt-1"
                  data-testid="drill-expiration-date"
                />
              </div>
              <div>
                <Label className="text-gray-700">Time Drill Conducted</Label>
                <Input
                  type="time"
                  value={formData.drillTime}
                  onChange={(e) => handleInputChange("drillTime", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700">Duration (Minutes) *</Label>
                <Input
                  type="number"
                  value={formData.durationMinutes}
                  onChange={(e) => handleInputChange("durationMinutes", e.target.value)}
                  className="mt-1"
                  placeholder="e.g., 15"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-gray-700">Type of Emergency Drill *</Label>
                <Select value={formData.drillType} onValueChange={(v) => handleInputChange("drillType", v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select drill type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DRILL_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.drillType === "Other" && (
                <div>
                  <Label className="text-gray-700">Specify Other Drill Type</Label>
                  <Input
                    value={formData.otherDrillType}
                    onChange={(e) => handleInputChange("otherDrillType", e.target.value)}
                    className="mt-1"
                    placeholder="Describe the drill type"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Participants */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3">Participants</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-gray-700">Names of Staff & Adults Who Participated</Label>
                <Textarea
                  value={formData.participatingStaff}
                  onChange={(e) => handleInputChange("participatingStaff", e.target.value)}
                  className="mt-1 min-h-[80px]"
                  placeholder="List all facility staff and other adults who participated in the drill"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Total Number of Children Participating</Label>
                  <Input
                    type="number"
                    value={formData.childrenCount}
                    onChange={(e) => handleInputChange("childrenCount", e.target.value)}
                    className="mt-1"
                    placeholder="Enter number"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Total Number of Adults & Children</Label>
                  <Input
                    type="number"
                    value={formData.totalParticipants}
                    onChange={(e) => handleInputChange("totalParticipants", e.target.value)}
                    className="mt-1"
                    placeholder="Enter total"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Special Accommodations */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3">Special Accommodations</h3>
            <div>
              <Label className="text-gray-700">Accommodations for Infants, Toddlers, or Children with Special Needs</Label>
              <Textarea
                value={formData.specialAccommodations}
                onChange={(e) => handleInputChange("specialAccommodations", e.target.value)}
                className="mt-1 min-h-[80px]"
                placeholder="Describe any special accommodations used during the drill"
              />
            </div>
          </div>

          {/* Problems & Observations */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3">Problems & Observations</h3>
            <div>
              <Label className="text-gray-700">List Problems Encountered or Observations (For updating Emergency Plan)</Label>
              <Textarea
                value={formData.problemsObservations}
                onChange={(e) => handleInputChange("problemsObservations", e.target.value)}
                className="mt-1 min-h-[100px]"
                placeholder="Document any difficulties, unexpected issues, or positive observations during the drill"
              />
            </div>
          </div>

          {/* Signature Section */}
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3">Signature</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700">Name</Label>
                <Input
                  value={formData.signatureName}
                  onChange={(e) => handleInputChange("signatureName", e.target.value)}
                  className="mt-1"
                  placeholder="Print name"
                />
              </div>
              <div>
                <Label className="text-gray-700">Position/Title</Label>
                <Input
                  value={formData.signaturePosition}
                  onChange={(e) => handleInputChange("signaturePosition", e.target.value)}
                  className="mt-1"
                  placeholder="e.g., Director, Lead Teacher"
                />
              </div>
              <div>
                <Label className="text-gray-700">Date</Label>
                <Input
                  type="date"
                  value={formData.signatureDate}
                  onChange={(e) => handleInputChange("signatureDate", e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Drill Log
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Drill History ── */}
      <Card className="border-blue-100 shadow-md">
        <CardHeader className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-t-xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-white text-lg font-bold">Emergency Drill History</CardTitle>
                <p className="text-blue-200 text-xs mt-0.5">Frequency varies by state — set your own expiration date</p>
              </div>
            </div>
            <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-full">
              {drillLogs.length} {drillLogs.length === 1 ? "record" : "records"}
            </span>
          </div>

          {/* Compliance Status Banner */}
          {drillLogs.length > 0 && (() => {
            const sortedDates = drillLogs
              .map(l => {
                try { return new Date(l.drillDate); } catch { return null; }
              })
              .filter(Boolean)
              .sort((a, b) => b - a);
            const lastDrill = sortedDates[0];
            const daysSince = lastDrill ? Math.floor((new Date() - lastDrill) / (1000 * 60 * 60 * 24)) : null;
            const nextDue = lastDrill ? new Date(lastDrill.getTime() + 365 * 24 * 60 * 60 * 1000) : null;
            const isOverdue = daysSince > 365;
            const isExpiring = daysSince > 335 && !isOverdue;
            const statusColor = isOverdue ? "bg-red-500/30 border-red-400/50" : isExpiring ? "bg-yellow-500/30 border-yellow-400/50" : "bg-green-500/30 border-green-400/50";
            const statusText = isOverdue ? "OVERDUE" : isExpiring ? "DUE SOON" : "COMPLIANT";
            const statusTextColor = isOverdue ? "text-red-200" : isExpiring ? "text-yellow-200" : "text-green-200";
            return (
              <div className={`mt-3 flex items-center justify-between px-4 py-2.5 rounded-xl border ${statusColor}`}>
                <div className="flex items-center gap-3">
                  <span className={`font-extrabold text-sm tracking-widest ${statusTextColor}`}>{statusText}</span>
                  <span className="text-white/80 text-xs">Last drill: <strong className="text-white">{lastDrill?.toLocaleDateString()}</strong> ({daysSince} days ago)</span>
                </div>
                <span className="text-white/80 text-xs">Next due: <strong className="text-white">{nextDue?.toLocaleDateString()}</strong></span>
              </div>
            );
          })()}
        </CardHeader>

        <CardContent className="p-0">
          {drillLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <CheckCircle className="w-10 h-10 mb-3 opacity-30" />
              <p className="font-medium">No drill logs recorded yet</p>
              <p className="text-sm mt-1">Complete the form above to log your first emergency drill</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-50 border-b border-blue-100">
                    <th className="text-left px-4 py-3 text-blue-800 font-semibold">Date</th>
                    <th className="text-left px-4 py-3 text-blue-800 font-semibold">Drill Type</th>
                    <th className="text-left px-4 py-3 text-blue-800 font-semibold">Time</th>
                    <th className="text-left px-4 py-3 text-blue-800 font-semibold">Duration</th>
                    <th className="text-left px-4 py-3 text-blue-800 font-semibold">Participants</th>
                    <th className="text-left px-4 py-3 text-blue-800 font-semibold">Days Ago</th>
                    <th className="text-left px-4 py-3 text-blue-800 font-semibold">Notes</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {drillLogs.map((log, idx) => {
                    const drillDate = new Date(log.drillDate);
                    const daysSince = Math.floor((new Date() - drillDate) / (1000 * 60 * 60 * 24));
                    const isOverdue = daysSince > 365;
                    const isExpiring = daysSince > 335 && !isOverdue;
                    const rowBg = idx % 2 === 0 ? "bg-white" : "bg-gray-50";
                    return (
                      <tr key={log.id} className={`${rowBg} border-b border-gray-100 hover:bg-blue-50/50 transition-colors`} data-testid={`drill-log-row-${log.id}`}>
                        <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                          {drillDate.toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{log.drillType}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{log.drillTime || "—"}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{log.durationMinutes ? `${log.durationMinutes} min` : "—"}</td>
                        <td className="px-4 py-3 text-gray-600">{log.totalParticipants || log.childrenCount || 0}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                            isOverdue ? "bg-red-100 text-red-700" :
                            isExpiring ? "bg-yellow-100 text-yellow-700" :
                            "bg-green-100 text-green-700"
                          }`}>
                            {isOverdue ? "Overdue" : isExpiring ? "Due Soon" : `${daysSince} days ago`}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 max-w-xs truncate text-xs">{log.problemsObservations || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => loadPreviousLog(log)}
                              className="h-7 px-2 text-blue-600 border-blue-200 hover:bg-blue-50 text-xs"
                              data-testid={`load-log-${log.id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />Load
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(log.id)}
                              className="h-7 px-2 text-red-500 border-red-200 hover:bg-red-50"
                              data-testid={`delete-log-${log.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmergencyDrillLogTab;
